import React, { useEffect, useMemo, useState } from "react"
import { Button } from "./ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card"
import { cn } from "../lib/utils"
import { appConfirm } from "../lib/app-dialogs"
import { usePageLoading } from "../lib/page-loading"

function csrfToken() {
  return document.querySelector("meta[name='csrf-token']")?.getAttribute("content") || ""
}

async function fetchJson(url, options = {}) {
  const res = await fetch(url, {
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "X-CSRF-Token": csrfToken(),
      ...(options.headers || {}),
    },
    ...options,
  })
  const contentType = res.headers.get("content-type") || ""
  const data = contentType.includes("application/json") ? await res.json() : null
  if (!res.ok) {
    const message = data?.message || data?.error || `Request failed (${res.status})`
    const details = data?.errors ? (Array.isArray(data.errors) ? data.errors.join(", ") : JSON.stringify(data.errors)) : ""
    throw new Error(details ? `${message}: ${details}` : message)
  }
  return data
}

function centsToAmount(cents) {
  if (cents === null || cents === undefined) return ""
  return (Number(cents) / 100).toFixed(2)
}

function amountToCents(value) {
  const n = Number.parseFloat(String(value || "0"))
  if (Number.isNaN(n)) return 0
  return Math.round(n * 100)
}

function formatInr(cents) {
  return new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" }).format(Number(cents || 0) / 100)
}

function monthBounds(date = new Date()) {
  const y = date.getFullYear()
  const m = date.getMonth()
  const start = new Date(y, m, 1)
  const end = new Date(y, m + 1, 0)
  const pad = (n) => String(n).padStart(2, "0")
  const iso = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
  return { period_start: iso(start), period_end: iso(end), pay_date: iso(end) }
}

const EMPTY_RUN = { ...monthBounds(), notes: "" }

function lineFromApi(row) {
  return {
    id: row.id,
    employee_id: String(row.employee_id),
    employee_name: row.employee?.name || "",
    employee_number: row.employee?.employee_number || "",
    base: centsToAmount(row.base_cents),
    allowance: centsToAmount(row.allowance_cents),
    bonus: centsToAmount(row.bonus_cents),
    pf: centsToAmount(row.pf_cents),
    esi: centsToAmount(row.esi_cents),
    tds: centsToAmount(row.tds_cents),
    other_deduction: centsToAmount(row.other_deduction_cents),
    gross_cents: row.gross_cents,
    net_cents: row.net_cents,
  }
}

function lineToApi(line) {
  return {
    employee_id: line.employee_id,
    base_cents: amountToCents(line.base),
    allowance_cents: amountToCents(line.allowance),
    bonus_cents: amountToCents(line.bonus),
    pf_cents: amountToCents(line.pf),
    esi_cents: amountToCents(line.esi),
    tds_cents: amountToCents(line.tds),
    other_deduction_cents: amountToCents(line.other_deduction),
  }
}

function TextInput({ label, value, onChange, type = "text", readOnly = false }) {
  return (
    <label className="grid gap-0.5 text-xs">
      <span className="text-muted-foreground">{label}</span>
      <input
        type={type}
        value={value ?? ""}
        readOnly={readOnly}
        disabled={readOnly}
        onChange={(e) => onChange(e.target.value)}
        className={cn(
          "w-full rounded-lg border border-input bg-card px-2.5 py-1.5 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-ring/20",
          readOnly && "cursor-default bg-muted/30 text-muted-foreground"
        )}
      />
    </label>
  )
}

function payslipHref(runId, employeeId) {
  return `/api/accounting/payroll_runs/${runId}/payslip?employee_id=${encodeURIComponent(employeeId)}`
}

function registerHref(runId) {
  return `/api/accounting/payroll_runs/${runId}/register_pdf`
}

function statusBadge(status) {
  const styles = {
    draft: "bg-amber-50 text-amber-800 border-amber-200",
    posted: "bg-blue-50 text-blue-800 border-blue-200",
    paid: "bg-emerald-50 text-emerald-800 border-emerald-200",
    void: "bg-muted text-muted-foreground border-border",
  }
  return (
    <span className={cn("rounded border px-1.5 py-0.5 text-[10px] font-medium uppercase", styles[status] || styles.draft)}>
      {status}
    </span>
  )
}

export function PayrollPanel() {
  const [runs, setRuns] = useState([])
  const [selectedId, setSelectedId] = useState(null)
  const [runForm, setRunForm] = useState(EMPTY_RUN)
  const [lines, setLines] = useState([])
  const [loading, setLoading] = useState(true)
  usePageLoading(loading)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState("")
  const [creating, setCreating] = useState(false)

  const selected = useMemo(() => runs.find((r) => String(r.id) === String(selectedId)), [runs, selectedId])
  const isDraft = selected?.status === "draft"
  const isPosted = selected?.status === "posted"

  async function loadRuns() {
    const data = await fetchJson("/api/accounting/payroll_runs")
    const list = Array.isArray(data) ? data : []
    setRuns(list)
    return list
  }

  async function loadRun(id) {
    const data = await fetchJson(`/api/accounting/payroll_runs/${id}`)
    setSelectedId(String(data.id))
    setRunForm({
      period_start: data.period_start,
      period_end: data.period_end,
      pay_date: data.pay_date,
      notes: data.notes || "",
    })
    setLines((data.payroll_run_lines || []).map(lineFromApi))
    setRuns((prev) => {
      const rest = prev.filter((r) => String(r.id) !== String(data.id))
      return [data, ...rest]
    })
    return data
  }

  useEffect(() => {
    let alive = true
    ;(async () => {
      try {
        setLoading(true)
        setError("")
        await loadRuns()
      } catch (e) {
        if (alive) setError(e.message || "Failed to load payroll runs")
      } finally {
        if (alive) setLoading(false)
      }
    })()
    return () => {
      alive = false
    }
  }, [])

  function startNew() {
    setCreating(true)
    setSelectedId(null)
    setRunForm(EMPTY_RUN)
    setLines([])
    setError("")
  }

  function cancelNew() {
    setCreating(false)
    setRunForm(EMPTY_RUN)
    setLines([])
  }

  async function onCreateRun(e) {
    e.preventDefault()
    try {
      setBusy(true)
      setError("")
      const body = JSON.stringify({ payroll_run: runForm })
      const data = await fetchJson("/api/accounting/payroll_runs", { method: "POST", body })
      setCreating(false)
      await loadRuns()
      await loadRun(data.id)
    } catch (err) {
      setError(err.message || "Create failed")
    } finally {
      setBusy(false)
    }
  }

  async function onSaveRun() {
    if (!selectedId || !isDraft) return
    try {
      setBusy(true)
      setError("")
      const body = JSON.stringify({
        payroll_run: {
          ...runForm,
          lines: lines.map(lineToApi),
        },
      })
      await fetchJson(`/api/accounting/payroll_runs/${selectedId}`, { method: "PATCH", body })
      await loadRun(selectedId)
    } catch (err) {
      setError(err.message || "Save failed")
    } finally {
      setBusy(false)
    }
  }

  async function onBuildLines() {
    if (!selectedId) return
    try {
      setBusy(true)
      setError("")
      await fetchJson(`/api/accounting/payroll_runs/${selectedId}/build_lines`, { method: "POST", body: "{}" })
      await loadRun(selectedId)
    } catch (err) {
      setError(err.message || "Build lines failed")
    } finally {
      setBusy(false)
    }
  }

  async function onPostRun() {
    if (!selectedId) return
    if (
      !(await appConfirm("Post this payroll run to the general ledger (salary expense / salaries payable)?", {
        title: "Post payroll",
        confirmLabel: "Post",
      }))
    )
      return
    try {
      setBusy(true)
      setError("")
      await fetchJson(`/api/accounting/payroll_runs/${selectedId}/post_run`, { method: "POST", body: "{}" })
      await loadRun(selectedId)
    } catch (err) {
      setError(err.message || "Post failed")
    } finally {
      setBusy(false)
    }
  }

  async function onMarkPaid() {
    if (!selectedId) return
    if (
      !(await appConfirm("Record bank payment for this payroll (debit salaries payable, credit cash)?", {
        title: "Mark paid",
        confirmLabel: "Record payment",
      }))
    )
      return
    try {
      setBusy(true)
      setError("")
      await fetchJson(`/api/accounting/payroll_runs/${selectedId}/mark_paid`, { method: "POST", body: "{}" })
      await loadRun(selectedId)
    } catch (err) {
      setError(err.message || "Mark paid failed")
    } finally {
      setBusy(false)
    }
  }

  async function onDeleteRun() {
    if (!selectedId || !isDraft) return
    if (!(await appConfirm("Delete this draft payroll run?", { title: "Delete", danger: true, confirmLabel: "Delete" }))) return
    try {
      setBusy(true)
      setError("")
      await fetchJson(`/api/accounting/payroll_runs/${selectedId}`, { method: "DELETE" })
      setSelectedId(null)
      setLines([])
      setRunForm(EMPTY_RUN)
      await loadRuns()
    } catch (err) {
      setError(err.message || "Delete failed")
    } finally {
      setBusy(false)
    }
  }

  function updateLine(index, field, value) {
    setLines((prev) => prev.map((row, i) => (i === index ? { ...row, [field]: value } : row)))
  }

  const totals = useMemo(() => {
    let gross = 0
    let net = 0
    lines.forEach((l) => {
      const g =
        amountToCents(l.base) + amountToCents(l.allowance) + amountToCents(l.bonus)
      const d =
        amountToCents(l.pf) + amountToCents(l.esi) + amountToCents(l.tds) + amountToCents(l.other_deduction)
      gross += g
      net += g - d
    })
    return { gross, net }
  }, [lines])

  return (
    <div className="flex flex-col gap-6">
      <Card>
        <CardHeader className="flex-row items-center justify-between gap-3 space-y-0">
          <CardTitle>Payroll runs</CardTitle>
          <Button type="button" variant="outline" onClick={startNew} disabled={creating}>
            New payroll run
          </Button>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-xs text-muted-foreground">
            Set salary structures under{" "}
            <a href="/employee-management/salary-structures" className="font-medium text-primary hover:underline">
              Employee management → Salary structures
            </a>
            . Map payroll GL accounts in{" "}
            <a href="/accountant/bookkeeping-settings" className="font-medium text-primary hover:underline">
              Bookkeeping settings
            </a>
            .
          </p>
          {error ? <div className="rounded border border-red-200 bg-red-50 px-2 py-1.5 text-xs text-red-700">{error}</div> : null}
          {loading ? (
            <p className="text-xs text-muted-foreground">Loading…</p>
          ) : (
            <ul className="divide-y divide-border rounded-lg border border-border/90 text-xs">
              {runs.length === 0 ? (
                <li className="p-3 text-muted-foreground">No payroll runs yet.</li>
              ) : (
                runs.map((r) => (
                  <li key={r.id}>
                    <button
                      type="button"
                      className={cn(
                        "flex w-full items-center justify-between gap-2 px-3 py-2 text-left hover:bg-muted/40",
                        String(selectedId) === String(r.id) && "bg-muted/50"
                      )}
                      onClick={() => {
                        setCreating(false)
                        loadRun(r.id).catch((e) => setError(e.message))
                      }}
                    >
                      <span>
                        <span className="font-medium text-foreground">{r.run_number}</span>
                        <span className="mt-0.5 block text-muted-foreground">
                          {r.period_start} → {r.period_end} · pay {r.pay_date}
                        </span>
                      </span>
                      <span className="flex flex-col items-end gap-1">
                        {statusBadge(r.status)}
                        <span className="font-medium">{formatInr(r.total_net_cents)}</span>
                      </span>
                    </button>
                  </li>
                ))
              )}
            </ul>
          )}
        </CardContent>
      </Card>

      {creating ? (
        <Card>
          <CardHeader>
            <CardTitle>New payroll run</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={onCreateRun} className="grid gap-3 md:grid-cols-3">
              <TextInput label="Period start" type="date" value={runForm.period_start} onChange={(v) => setRunForm((f) => ({ ...f, period_start: v }))} />
              <TextInput label="Period end" type="date" value={runForm.period_end} onChange={(v) => setRunForm((f) => ({ ...f, period_end: v }))} />
              <TextInput label="Pay date" type="date" value={runForm.pay_date} onChange={(v) => setRunForm((f) => ({ ...f, pay_date: v }))} />
              <label className="md:col-span-3 grid gap-0.5 text-xs">
                <span className="text-muted-foreground">Notes</span>
                <textarea
                  value={runForm.notes}
                  onChange={(e) => setRunForm((f) => ({ ...f, notes: e.target.value }))}
                  className="min-h-[60px] w-full rounded-lg border border-input bg-card px-2.5 py-1.5 text-sm"
                />
              </label>
              <div className="md:col-span-3 flex gap-2">
                <Button type="submit" disabled={busy}>
                  {busy ? "…" : "Create draft"}
                </Button>
                <Button type="button" variant="ghost" onClick={cancelNew}>
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      ) : null}

      {selected && !creating ? (
        <Card>
          <CardHeader className="flex-row flex-wrap items-center justify-between gap-3 space-y-0">
            <div>
              <CardTitle>{selected.run_number}</CardTitle>
              <p className="mt-1 text-xs text-muted-foreground">
                {selected.period_start} → {selected.period_end} · pay {selected.pay_date} {statusBadge(selected.status)}
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              {isDraft ? (
                <>
                  <Button type="button" variant="outline" size="sm" onClick={onBuildLines} disabled={busy}>
                    Build from salary structures
                  </Button>
                  <Button type="button" size="sm" onClick={onSaveRun} disabled={busy}>
                    Save lines
                  </Button>
                  <Button type="button" size="sm" onClick={onPostRun} disabled={busy || lines.length === 0}>
                    Post to GL
                  </Button>
                  <Button type="button" variant="ghost" size="sm" onClick={onDeleteRun} disabled={busy}>
                    Delete
                  </Button>
                </>
              ) : null}
              {isPosted ? (
                <Button type="button" size="sm" onClick={onMarkPaid} disabled={busy}>
                  Mark paid
                </Button>
              ) : null}
              {lines.length > 0 ? (
                <a
                  href={registerHref(selectedId)}
                  className="inline-flex h-8 items-center rounded-lg border border-input bg-card px-3 text-xs font-medium hover:bg-muted/50"
                >
                  Register PDF
                </a>
              ) : null}
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {isDraft ? (
              <div className="grid gap-3 md:grid-cols-3">
                <TextInput label="Period start" type="date" value={runForm.period_start} onChange={(v) => setRunForm((f) => ({ ...f, period_start: v }))} />
                <TextInput label="Period end" type="date" value={runForm.period_end} onChange={(v) => setRunForm((f) => ({ ...f, period_end: v }))} />
                <TextInput label="Pay date" type="date" value={runForm.pay_date} onChange={(v) => setRunForm((f) => ({ ...f, pay_date: v }))} />
              </div>
            ) : null}

            {lines.length === 0 ? (
              <p className="text-xs text-muted-foreground">No lines yet. Use “Build from salary structures” for active employees with a salary structure.</p>
            ) : (
              <div className="overflow-x-auto rounded-lg border border-border/90">
                <table className="w-full min-w-[720px] text-left text-xs">
                  <thead className="border-b bg-muted/30 text-muted-foreground">
                    <tr>
                      <th className="px-2 py-1.5">Employee</th>
                      <th className="px-2 py-1.5">Base</th>
                      <th className="px-2 py-1.5">Allowance</th>
                      <th className="px-2 py-1.5">Bonus</th>
                      <th className="px-2 py-1.5">PF</th>
                      <th className="px-2 py-1.5">ESI</th>
                      <th className="px-2 py-1.5">TDS</th>
                      <th className="px-2 py-1.5">Other ded.</th>
                      <th className="px-2 py-1.5 text-right">Net</th>
                      <th className="px-2 py-1.5">Payslip</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lines.map((line, idx) => {
                      const g = amountToCents(line.base) + amountToCents(line.allowance) + amountToCents(line.bonus)
                      const d = amountToCents(line.pf) + amountToCents(line.esi) + amountToCents(line.tds) + amountToCents(line.other_deduction)
                      return (
                        <tr key={line.id || `${line.employee_id}-${idx}`} className="border-b border-border/60">
                          <td className="px-2 py-1.5">
                            <div className="font-medium">{line.employee_name}</div>
                            <div className="text-muted-foreground">{line.employee_number}</div>
                          </td>
                          {["base", "allowance", "bonus", "pf", "esi", "tds", "other_deduction"].map((field) => (
                            <td key={field} className="px-1 py-1">
                              <input
                                type="text"
                                readOnly={!isDraft}
                                disabled={!isDraft}
                                value={line[field]}
                                onChange={(e) => updateLine(idx, field, e.target.value)}
                                className="w-20 rounded border border-input px-1 py-0.5 text-right"
                              />
                            </td>
                          ))}
                          <td className="px-2 py-1.5 text-right font-medium">{formatInr(g - d)}</td>
                          <td className="px-2 py-1.5">
                            <a
                              href={payslipHref(selectedId, line.employee_id)}
                              className="text-primary hover:underline"
                              title="Download payslip PDF"
                            >
                              PDF
                            </a>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="bg-muted/20 font-medium">
                      <td className="px-2 py-2" colSpan={8}>
                        Totals
                      </td>
                      <td className="px-2 py-2 text-right">{formatInr(totals.net)}</td>
                      <td />
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}

            {selected.posted_at ? (
              <p className="text-[11px] text-muted-foreground">Posted {new Date(selected.posted_at).toLocaleString()}</p>
            ) : null}
            {selected.paid_at ? (
              <p className="text-[11px] text-muted-foreground">Paid {new Date(selected.paid_at).toLocaleString()}</p>
            ) : null}
          </CardContent>
        </Card>
      ) : null}
    </div>
  )
}
